/**
 * MER-706: the staff variance register page's pure logic.
 *
 * public.board_variance (migration 0058) holds, for every line of a client's
 * executive workbook and every month, the client's figure beside the sum of
 * OUR catalogue inputs for the same sheet and month, with the gap, a band
 * verdict and an independence class. The pipeline (audit/board_variance.py)
 * computes every value and verdict. This module only reads them back: it
 * filters, groups, counts and formats. It never grades a row, never derives a
 * band, and never turns a missing value into a zero.
 *
 * Staff only. The table's sole read policy is public.is_staff(), and the page
 * sits in the dashboard's Internal block behind the staff session. Nothing
 * here is imported by the client-visible Executive page.
 *
 * Plain .mjs so node --test drives it with synthetic rows (the execKpi.mjs
 * precedent); boardVariance.d.mts types it for the page and the data module.
 */

import { fmtValue, monthLabel, sheetLabel } from "./execKpi.mjs";

export const BOARD_VARIANCE_TABLE = "board_variance";

/**
 * The columns the page reads. Every name is a column of public.board_variance
 * in 0058 (pinned by scripts/board-variance-register.test.mjs).
 */
export const BOARD_VARIANCE_COLUMNS = [
  "check_kind",
  "sheet",
  "client_label",
  "period",
  "division",
  "equipment_type",
  "is_rollup",
  "maps_to_kind",
  "metric_id",
  "metric_version",
  "metric_aggregation",
  "input_id",
  "compared_inputs",
  "client_value",
  "client_unit",
  "client_provenance",
  "our_value",
  "our_unit",
  "our_member_grains",
  "our_excluded_grains",
  "our_excluded_value",
  "our_data_quality",
  "reference_value",
  "gap",
  "gap_pct",
  "band_family",
  "band_pct",
  "band_source",
  "verdict",
  "verdict_reason",
  "independence_class",
  "client_source",
  "basis_note",
  "workbook_file",
  "workbook_latest_period",
  "computed_at",
].join(",");

/**
 * Paging order: the table's primary key minus tenant_id (bound separately),
 * so every page boundary is stable and no row is read twice or skipped.
 */
export const REGISTER_ORDER = Object.freeze(["check_kind", "sheet", "client_label", "period"]);

export const REGISTER_PAGE_SIZE = 1000;
/** 64 pages x 1,000 rows, about ten times one workbook's register. */
export const REGISTER_MAX_PAGES = 64;

/** The publish log (public.sync_log) entity keys the header reads. */
export const WORKBOOK_ENTITY = "EXEC_KPI";
export const REGISTER_ENTITY = "BOARD_VARIANCE";

export const CHECK_VARIANCE = "variance";
export const CHECK_ROLLUP_SELF = "rollup_self_check";
/**
 * AJ 2026-09-30 (migration 0097): the line computed with the client's OWN
 * definition beside the workbook figure (audit/client_definitions.py). It
 * reads like a variance row; its target is the definition, not a catalogue
 * metric.
 */
export const CHECK_CLIENT_DEFINITION = "client_definition";
export const CHECK_KINDS = Object.freeze([
  CHECK_VARIANCE,
  CHECK_ROLLUP_SELF,
  CHECK_CLIENT_DEFINITION,
]);

/** Every verdict 0058 allows, variance verdicts first, in display rank. */
export const VERDICTS = Object.freeze([
  "out_of_band",
  "in_band",
  "no_band",
  "not_compared",
  "no_our_value",
  "no_client_inputs",
  "client_not_comparable",
  "unmapped",
  "mismatch",
  "reconciled",
  "could_not_check",
]);

export const VERDICT_LABELS = Object.freeze({
  in_band: "In band",
  out_of_band: "Out of band",
  no_band: "No band agreed",
  not_compared: "Not compared",
  no_our_value: "No value of ours",
  no_client_inputs: "No client inputs",
  client_not_comparable: "Client figure not comparable",
  unmapped: "Unmapped",
  reconciled: "Reconciled",
  mismatch: "Mismatch",
  could_not_check: "Could not check",
});

/**
 * How a verdict chip reads. good and bad are the only verdicts that grade a
 * line; every other verdict is shown without colour so it cannot be read as
 * a pass or a fail.
 */
export const VERDICT_TONES = Object.freeze({
  in_band: "good",
  reconciled: "good",
  out_of_band: "bad",
  mismatch: "bad",
  no_band: "neutral",
  not_compared: "neutral",
  no_our_value: "neutral",
  no_client_inputs: "neutral",
  client_not_comparable: "neutral",
  unmapped: "neutral",
  could_not_check: "neutral",
});

export const INDEPENDENCE_CLASSES = Object.freeze(["independent", "same_system", "unconfirmed"]);

export const INDEPENDENCE_LABELS = Object.freeze({
  independent: "Independent",
  same_system: "Same-system extract",
  unconfirmed: "Unconfirmed",
});

/** What a comparison of each class can and cannot show. */
export const INDEPENDENCE_NOTES = Object.freeze({
  independent:
    "The client's figure comes from a source outside the system we read, such as their closed books. A match here is a real outside check.",
  same_system:
    "The client's figure and ours are two extracts of the same system. A match shows our transforms agree with their report. It does not prove the underlying data is true: a mis-coded work order would pass both.",
  unconfirmed:
    "Where the client got this figure is not confirmed yet. Treat a match as unproven until it is.",
});

/** Roll-up self-check rows compare the client's roll-up with the client's own division sheets. */
export const SELF_CHECK_CLASS_LABEL = "Client vs client";

export const SAME_SYSTEM_TITLE = "A same-system match is not independent proof";

export const UNITS = Object.freeze(["usd", "usd_per_unit", "fraction", "count", "number"]);

/**
 * Which way a gap runs. The pipeline writes gap = ours - client and gap_pct =
 * gap / |client| on a variance row, and gap = roll-up - division sum on a
 * roll-up self-check row (audit/board_variance.py, pinned by the register
 * test). A negative gap on a variance row means our figure is the lower one.
 */
export const GAP_HEADER = "Gap (ours - client)";
export const GAP_PCT_HEADER = "Gap % (of client)";
export const SELF_CHECK_GAP_HEADER = "Gap (roll-up - divisions)";
export const GAP_SIGN_NOTE =
  "Gap is ours minus the client's figure, so a negative gap means ours is lower. Gap % is that gap over the client's figure.";

/**
 * The data-quality stamps normalize.py puts on a kpi_summary row, which the
 * pipeline copies into our_data_quality (comma-joined when our value sums
 * several rows). MER-722: only the stamps that bear on a column the line
 * actually read reach this field, so a labour-coverage stamp is not named
 * beside a revenue comparison. The pipeline still grades a stamped row, so the
 * page names the stamp beside our value. An unknown stamp always reaches this
 * field and is shown as it is, unexplained.
 */
export const DATA_QUALITY_NOTES = Object.freeze({
  FEED_ANOMALY: "disposal volume far above its own trailing history; disposal values withheld",
  FEED_INCOMPLETE: "the invoice export for the month looks too thin",
  PARTIAL_DATA:
    "roll-off disposal ticket coverage under 50%; disposal figures are published " +
    "with the share of hauls they cover stated beside them",
  SUPPRESSED: "labor-hours coverage under 40%; labor KPIs withheld",
});

export const REGISTER_MISSING_TITLE = "Register not available yet";
export const REGISTER_MISSING_CAPTION =
  "The board_variance table is not in this database yet (migration 0058). Once it is applied, the next refresh builds the register.";
export const REGISTER_ERROR_TITLE = "Register could not be read";
export const REGISTER_ERROR_CAPTION =
  "The read failed. The details are in the browser console; nothing on this page was guessed.";
export const NO_MATCH_COPY = "No register rows match these filters.";
export const PUBLISH_LOG_CAVEAT =
  "Every refresh republishes the stored workbook, so the publish log shows when its rows were last pushed, not when a new file arrived. The file name is the one the register was built from.";

export function truncatedRegisterCopy(rowCount) {
  return `Showing the first ${Number(rowCount).toLocaleString("en-US")} register rows. The register holds more than this page reads, so the counts below are incomplete.`;
}

/**
 * The empty-register card, worded by whether the tenant has an executive
 * workbook at all (the ExecKpiProvider probe the shell already makes).
 *
 * @param {string} execAvailability  "pending" | "available" | "empty" | "missing" | "error"
 * @returns {{ title: string, caption: string }}
 */
export function emptyRegisterCopy(execAvailability) {
  if (execAvailability === "available") {
    return {
      title: "Register not built yet",
      caption:
        "This customer's executive workbook is loaded, but no refresh has built the register since the table was added. The next scheduled or manual refresh builds it.",
    };
  }
  if (execAvailability === "empty" || execAvailability === "missing") {
    return {
      title: "No executive workbook for this customer",
      caption:
        "The register sets a client's own workbook beside our numbers. This customer has no workbook loaded, so there is nothing to compare.",
    };
  }
  return {
    title: "No register rows for this customer yet",
    caption:
      "Either no executive workbook is loaded for this customer, or no refresh has built the register since the table was added.",
  };
}

/* ------------------------------------------------------------------ */
/* Small coercions                                                     */
/* ------------------------------------------------------------------ */

function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function text(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function isMonthKey(v) {
  return typeof v === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
}

/* ------------------------------------------------------------------ */
/* Row helpers                                                         */
/* ------------------------------------------------------------------ */

/** True for a register row the customer's line map ties to a catalogue metric or input. */
export function isMapped(row) {
  if (row && row.check_kind === CHECK_CLIENT_DEFINITION) return true;
  return !!row && (row.maps_to_kind === "metric" || row.maps_to_kind === "input");
}

/**
 * The catalogue target a row maps to, as a filter key ("metric:<id>" or
 * "input:<id>"), or null for an unmapped or self-check row.
 */
export function targetKey(row) {
  if (!row) return null;
  if (row.maps_to_kind === "metric" && text(row.metric_id)) return `metric:${row.metric_id}`;
  if (row.maps_to_kind === "input" && text(row.input_id)) return `input:${row.input_id}`;
  if (row.check_kind === CHECK_CLIENT_DEFINITION && text(row.compared_inputs)) {
    return `definition:${String(row.compared_inputs).split(":").slice(0, 2).join(":")}`;
  }
  return null;
}

/** "metric:revenue_earned" -> "revenue_earned (metric)". */
export function targetLabel(key) {
  const m = /^(metric|input|definition):(.+)$/.exec(String(key ?? ""));
  if (m && m[1] === "definition") return `${m[2]} (client definition)`;
  return m ? `${m[2]} (${m[1]})` : String(key ?? "");
}

export function verdictLabel(verdict) {
  return VERDICT_LABELS[verdict] ?? String(verdict ?? "n/a");
}

export function verdictTone(verdict) {
  return VERDICT_TONES[verdict] ?? "neutral";
}

export function independenceLabel(row) {
  if (row && row.check_kind === CHECK_ROLLUP_SELF) return SELF_CHECK_CLASS_LABEL;
  const cls = row ? row.independence_class : null;
  return INDEPENDENCE_LABELS[cls] ?? "Unconfirmed";
}

/**
 * The caveat an in-band row carries unless its comparison is independent.
 * Null when the row is not in band or is independent.
 */
export function inBandCaveat(row) {
  if (!row || row.verdict !== "in_band") return null;
  if (row.independence_class === "independent") return null;
  return row.independence_class === "same_system"
    ? "Same system: not independent proof"
    : "Source unconfirmed: not independent proof";
}

/** The distinct data-quality stamps on our side of a row, in stored order. */
export function dataQualityFlags(row) {
  const raw = row ? text(row.our_data_quality) : null;
  if (raw === null) return [];
  const out = [];
  for (const part of raw.split(",")) {
    const flag = part.trim();
    if (flag !== "" && !out.includes(flag)) out.push(flag);
  }
  return out;
}

/**
 * "Our inputs flagged: FEED_INCOMPLETE (...)" for a row whose value of ours
 * came from stamped kpi_summary rows, or null when nothing is stamped.
 */
export function dataQualityNote(row) {
  const flags = dataQualityFlags(row);
  if (flags.length === 0) return null;
  const named = flags.map((f) => (DATA_QUALITY_NOTES[f] ? `${f} (${DATA_QUALITY_NOTES[f]})` : f));
  return `Our inputs flagged: ${named.join("; ")}`;
}

/**
 * The caveat a graded verdict (in band or out of band) carries when our side
 * came from stamped rows. Null otherwise.
 */
export function flaggedVerdictCaveat(row) {
  if (!row || (row.verdict !== "in_band" && row.verdict !== "out_of_band")) return null;
  return dataQualityFlags(row).length > 0 ? "Graded on flagged inputs of ours" : null;
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

/** A value in its declared unit, the Executive page's unit defaults. Null for no number. */
export function fmtRegisterValue(value, unit) {
  return fmtValue(value, unit, null);
}

/**
 * A signed gap ("+$1,204", "-3", "0") in the unit it was computed in. A gap
 * that rounds to zero at the displayed precision carries no sign.
 */
export function fmtGap(value, unit) {
  const v = toNumber(value);
  if (v === null) return null;
  const abs = fmtValue(Math.abs(v), unit, null);
  if (abs === null) return null;
  if (!/[1-9]/.test(abs)) return abs;
  return `${v < 0 ? "-" : "+"}${abs}`;
}

/** A gap fraction as a signed percent with one decimal ("+1.0%", "-3.3%"). */
export function fmtGapPct(value) {
  const v = toNumber(value);
  if (v === null) return null;
  const body = `${(Math.abs(v) * 100).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
  if (!/[1-9]/.test(body)) return body;
  return `${v < 0 ? "-" : "+"}${body}`;
}

/** The band a verdict was judged against ("2% (MER-668)"), or null when none applies. */
export function fmtBand(row) {
  if (!row) return null;
  const band = toNumber(row.band_pct);
  const source = text(row.band_source);
  if (band !== null) {
    const pct = `${Number((band * 100).toFixed(2))}%`;
    return source ? `${pct} (${source})` : pct;
  }
  if (row.check_kind === CHECK_ROLLUP_SELF && source) return "Workbook roll-up tolerance";
  return null;
}

/**
 * What the "ours" cell shows. A ratio line never carries a value of ours (its
 * verdict comes from its inputs), and an empty value is never shown as zero:
 * the pipeline's reason is shown instead. `flag` names any data-quality stamp
 * on the kpi_summary rows our value came from.
 *
 * @returns {{ text: string | null, note: string | null, flag: string | null }}
 */
export function ourValueCell(row) {
  if (!row) return { text: null, note: null, flag: null };
  const flag = dataQualityNote(row);
  if (row.metric_aggregation === "ratio") {
    return { text: null, note: "Ratio: judged on its inputs, never on a finished ratio", flag };
  }
  const shown = fmtRegisterValue(row.our_value, row.our_unit ?? row.client_unit);
  if (shown === null) return { text: null, note: null, flag };
  const excluded = toNumber(row.our_excluded_grains);
  if (excluded !== null && excluded > 0) {
    const value = fmtGap(row.our_excluded_value, row.our_unit ?? row.client_unit);
    const grains = `${excluded} grain${excluded === 1 ? "" : "s"}`;
    return {
      text: shown,
      note: value
        ? `Not included: ${grains} outside the workbook sheets (${value})`
        : `Not included: ${grains} outside the workbook sheets`,
      flag,
    };
  }
  return { text: shown, note: null, flag };
}

/**
 * "2030-03-04T05:06:07.123456+00:00" -> "Mar 4, 2030, 05:06 UTC". Null for
 * anything that is not a timestamp.
 */
export function fmtTimestamp(iso) {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(iso)) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}, ${hh}:${mm} UTC`;
}

/* ------------------------------------------------------------------ */
/* Summary, filters, grouping                                          */
/* ------------------------------------------------------------------ */

function lineKey(row) {
  return `${row.sheet}\u0000${row.client_label}`;
}

function laterTimestamp(a, b) {
  if (a === null) return b;
  if (b === null) return a;
  return new Date(b).getTime() > new Date(a).getTime() ? b : a;
}

function compareSheets(a, b) {
  if (a.isRollup !== b.isRollup) return a.isRollup ? -1 : 1;
  return a.sheet.localeCompare(b.sheet);
}

/**
 * Whole-register facts for the header and the filter options. Rows that are
 * not register rows (no sheet, no label, no YYYY-MM period) are counted in
 * `skipped` and otherwise ignored.
 */
export function summarizeRegister(rows) {
  const sheets = new Map();
  const periods = new Set();
  const targets = new Set();
  const workbookPeriods = new Set();
  const workbookFiles = new Set();
  const lines = new Map();
  let computedAt = null;
  let varianceRows = 0;
  let selfCheckRows = 0;
  let skipped = 0;
  let latestPeriod = null;

  for (const row of rows ?? []) {
    if (
      !row ||
      typeof row.sheet !== "string" ||
      typeof row.client_label !== "string" ||
      !isMonthKey(row.period) ||
      !CHECK_KINDS.includes(row.check_kind)
    ) {
      skipped += 1;
      continue;
    }
    if (!sheets.has(row.sheet)) {
      sheets.set(row.sheet, {
        sheet: row.sheet,
        label: sheetLabel(row),
        isRollup: row.is_rollup === true,
      });
    }
    periods.add(row.period);
    if (isMonthKey(row.workbook_latest_period)) workbookPeriods.add(row.workbook_latest_period);
    if (text(row.workbook_file)) workbookFiles.add(row.workbook_file);
    if (fmtTimestamp(row.computed_at) !== null)
      computedAt = laterTimestamp(computedAt, row.computed_at);
    if (row.check_kind === CHECK_ROLLUP_SELF) {
      selfCheckRows += 1;
      continue;
    }
    varianceRows += 1;
    if (latestPeriod === null || row.period > latestPeriod) latestPeriod = row.period;
    const key = targetKey(row);
    if (key) targets.add(key);
    const line = lines.get(lineKey(row)) ?? {
      mapped: false,
      listed: false,
      independent: false,
    };
    if (isMapped(row)) line.mapped = true;
    // Every line the customer's line map lists carries its client source (the
    // pipeline's loader requires one); a line it does not list carries none.
    if (text(row.client_source) !== null) line.listed = true;
    if (row.independence_class === "independent") line.independent = true;
    lines.set(lineKey(row), line);
  }

  const lineValues = [...lines.values()];
  const workbookPeriodList = [...workbookPeriods].sort();
  return {
    varianceRows,
    selfCheckRows,
    skipped,
    sheets: [...sheets.values()].sort(compareSheets),
    periods: [...periods].sort().reverse(),
    targets: [...targets].sort((a, b) => a.localeCompare(b)),
    latestPeriod,
    workbookLatestPeriod: workbookPeriodList.length
      ? workbookPeriodList[workbookPeriodList.length - 1]
      : null,
    workbookPeriodCount: workbookPeriodList.length,
    workbookFiles: [...workbookFiles].sort(),
    computedAt,
    lines: {
      total: lineValues.length,
      mapped: lineValues.filter((l) => l.mapped).length,
      unmapped: lineValues.filter((l) => !l.mapped).length,
      notListed: lineValues.filter((l) => !l.mapped && !l.listed).length,
      independent: lineValues.filter((l) => l.independent).length,
    },
  };
}

/** The filters a staff reader lands on: every sheet and metric, the newest month, mapped lines only. */
export function defaultFilters(summary) {
  return {
    sheet: "",
    target: "",
    period: summary && isMonthKey(summary.latestPeriod) ? summary.latestPeriod : "",
    includeUnmapped: false,
  };
}

/**
 * Apply the page filters. An empty string means "all" for sheet, target and
 * period. Unmapped variance rows show only when includeUnmapped is true and
 * no target is chosen. Roll-up self-check rows follow the sheet and month
 * filters and are hidden while a metric is chosen (they map to none).
 *
 * @returns {{ variance: object[], selfCheck: object[] }}
 */
export function filterRegister(rows, filters) {
  const f = filters ?? {};
  const sheet = f.sheet ?? "";
  const target = f.target ?? "";
  const period = f.period ?? "";
  const includeUnmapped = f.includeUnmapped === true;
  const variance = [];
  const selfCheck = [];
  for (const row of rows ?? []) {
    if (!row || typeof row.sheet !== "string" || !isMonthKey(row.period)) continue;
    if (sheet && row.sheet !== sheet) continue;
    if (period && row.period !== period) continue;
    if (row.check_kind === CHECK_ROLLUP_SELF) {
      if (!target) selfCheck.push(row);
      continue;
    }
    if (row.check_kind !== CHECK_VARIANCE && row.check_kind !== CHECK_CLIENT_DEFINITION) continue;
    if (target) {
      if (targetKey(row) === target) variance.push(row);
      continue;
    }
    if (isMapped(row) || includeUnmapped) variance.push(row);
  }
  return { variance, selfCheck };
}

function verdictRank(verdict) {
  const i = VERDICTS.indexOf(verdict);
  return i === -1 ? VERDICTS.length : i;
}

function compareRows(a, b) {
  const r = verdictRank(a.verdict) - verdictRank(b.verdict);
  if (r !== 0) return r;
  return String(a.client_label).localeCompare(String(b.client_label));
}

/**
 * Group filtered rows by sheet (the roll-up first, then by sheet name) and
 * month (newest first). Within a month, variance rows run worst verdict
 * first (out of band, then in band, then the lines with no verdict), then by
 * label; self-check rows the same way.
 */
export function groupRegister(filtered) {
  const sheets = new Map();
  const add = (row, kind) => {
    let sheet = sheets.get(row.sheet);
    if (!sheet) {
      sheet = {
        sheet: row.sheet,
        label: sheetLabel(row),
        isRollup: row.is_rollup === true,
        months: new Map(),
      };
      sheets.set(row.sheet, sheet);
    }
    let month = sheet.months.get(row.period);
    if (!month) {
      month = { period: row.period, label: monthLabel(row.period), variance: [], selfCheck: [] };
      sheet.months.set(row.period, month);
    }
    month[kind].push(row);
  };
  for (const row of filtered?.variance ?? []) add(row, "variance");
  for (const row of filtered?.selfCheck ?? []) add(row, "selfCheck");
  return [...sheets.values()].sort(compareSheets).map((sheet) => ({
    sheet: sheet.sheet,
    label: sheet.label,
    isRollup: sheet.isRollup,
    months: [...sheet.months.values()]
      .sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : 0))
      .map((m) => ({
        ...m,
        variance: m.variance.slice().sort(compareRows),
        selfCheck: m.selfCheck.slice().sort(compareRows),
      })),
  }));
}

/**
 * Verdict counts over filtered variance rows, plus in-band rows split by
 * independence class so a green count is never read as outside proof.
 */
export function countVerdicts(varianceRows) {
  const verdicts = {};
  const inBandBy = { independent: 0, same_system: 0, unconfirmed: 0 };
  for (const row of varianceRows ?? []) {
    const v = typeof row?.verdict === "string" ? row.verdict : "unknown";
    verdicts[v] = (verdicts[v] ?? 0) + 1;
    if (v === "in_band") {
      const cls = INDEPENDENCE_CLASSES.includes(row.independence_class)
        ? row.independence_class
        : "unconfirmed";
      inBandBy[cls] += 1;
    }
  }
  return { verdicts, inBandBy, total: (varianceRows ?? []).length };
}

/* ------------------------------------------------------------------ */
/* Publish log                                                         */
/* ------------------------------------------------------------------ */

/**
 * Classify a one-row publish-log read (`select completed_at ... limit 1`).
 * Returns the timestamp when the reply carries a usable one, else null.
 */
export function classifyPublishTime(result) {
  if (!result || typeof result !== "object" || result.error) return null;
  const rows = Array.isArray(result.data) ? result.data : [];
  const raw = rows.length && rows[0] && typeof rows[0] === "object" ? rows[0].completed_at : null;
  return fmtTimestamp(raw) === null ? null : raw;
}

/**
 * Run a publish-log read and classify it. Resolves to a timestamp or null and
 * never throws; a failure goes to opts.warn, never onto the page.
 */
export async function readPublishTime(runQuery, opts = {}) {
  const warn = typeof opts.warn === "function" ? opts.warn : () => {};
  const what = typeof opts.label === "string" ? opts.label : "publish log";
  try {
    const result = await runQuery();
    const at = classifyPublishTime(result);
    if (at === null) {
      const reason =
        result && result.error && typeof result.error.message === "string"
          ? result.error.message
          : "no successful publish on record";
      warn(`${what} unavailable: ${reason}`);
    }
    return at;
  } catch (err) {
    warn(`${what} unavailable: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}
